"use client";

import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  Badge,
  Anchor,
  Divider,
  List,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCalendarStats,
  IconSparkles,
  IconChartBar,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import StatCard from "./StatCard";
import {
  SeasonDistributionChart,
  SearchTrendChart,
  PricingChart,
  DemographicsChart,
  MethodPreferenceChart,
  MotivationsChart,
} from "./charts";
import { LAST_UPDATED, NEXT_UPDATE, SAMPLE_SIZE, STAT_HEADLINES } from "./data";

export default function StatsContent() {
  return (
    <Box>
      {/* HERO — editorial, cream paper */}
      <Box
        style={{
          background:
            "linear-gradient(180deg, #faf6f0 0%, #f5ede2 100%)",
          paddingTop: 80,
          paddingBottom: 72,
          marginBottom: 56,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative color strip */}
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background:
              "linear-gradient(90deg, #FFB347 0%, #FBCEB1 16%, #F4C8D4 33%, #C9B6D4 50%, #C4A57A 66%, #A0522D 83%, #1A1A2E 100%)",
          }}
        />

        <Container size="lg">
          <Box ta="center" maw={820} mx="auto">
            <Text
              fz={11}
              tt="uppercase"
              mb={20}
              style={{
                letterSpacing: "0.32em",
                fontWeight: 600,
                color: "#A0522D",
              }}
            >
              The State of Color Analysis · Vol. {new Date().getFullYear()}
            </Text>

            <Title
              order={1}
              style={{
                fontSize: "clamp(2.6rem, 6.5vw, 4.4rem)",
                fontWeight: 300,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
                marginBottom: 24,
                color: "#1A1A2E",
                fontStyle: "italic",
              }}
            >
              The numbers behind a&nbsp;
              <span style={{ fontStyle: "normal", borderBottom: "2px solid #A0522D", paddingBottom: 2 }}>
                quiet revolution
              </span>
              &nbsp;in personal style.
            </Title>

            <Text
              size="lg"
              mb={32}
              style={{
                fontWeight: 300,
                letterSpacing: "0.01em",
                lineHeight: 1.7,
                color: "#4a3f3a",
                maxWidth: 680,
                margin: "0 auto 32px",
              }}
            >
              Season distribution, search trends, pricing, demographics — sourced from{" "}
              <strong style={{ color: "#1A1A2E" }}>{SAMPLE_SIZE.toLocaleString()}</strong>{" "}
              quiz completions and public search data. Refreshed monthly.
            </Text>

            <Group justify="center" gap="xs">
              <Badge
                variant="filled"
                size="lg"
                color="#1A1A2E"
                style={{ fontWeight: 500, letterSpacing: "0.05em" }}
                leftSection={<IconCalendarStats size={14} />}
              >
                Updated {LAST_UPDATED}
              </Badge>
              <Badge
                variant="outline"
                size="lg"
                style={{
                  borderColor: "#A0522D",
                  color: "#A0522D",
                  fontWeight: 500,
                  letterSpacing: "0.05em",
                }}
              >
                Next refresh: {NEXT_UPDATE}
              </Badge>
            </Group>
          </Box>

          {/* HEADLINE NUMBERS — editorial type, no boxes */}
          <Box
            mt={72}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 0,
              borderTop: "1px solid #d9cfbf",
              borderBottom: "1px solid #d9cfbf",
              background: "#fffdf9",
            }}
          >
            {STAT_HEADLINES.map((s, i) => (
              <Box
                key={s.label}
                p="xl"
                style={{
                  borderLeft: i === 0 ? "none" : "1px solid #ede4d3",
                  textAlign: "center",
                }}
              >
                <Text
                  fz={44}
                  fw={300}
                  style={{
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: "#1A1A2E",
                    fontStyle: "italic",
                  }}
                >
                  {s.value}
                </Text>
                <Text
                  fz={11}
                  mt={12}
                  c="dimmed"
                  style={{ lineHeight: 1.5, letterSpacing: "0.02em" }}
                >
                  {s.label}
                </Text>
              </Box>
            ))}
          </Box>

          <Text
            ta="center"
            fz={12}
            c="dimmed"
            mt={32}
            style={{ fontStyle: "italic", letterSpacing: "0.05em" }}
          >
            Free to cite · One-click embed on every chart · Please link back so readers find the latest figures.
          </Text>
        </Container>
      </Box>

      <Container size="lg" pb="xl">
        <Stack gap={48}>
          {/* ===== Season Distribution ===== */}
          <StatCard
            id="season-distribution"
            title="Population share of each 12-season color type"
            subtitle={`Self-reported quiz completions, n=${SAMPLE_SIZE.toLocaleString()}. Soft Summer is the most common at 13.1%; Bright Winter the rarest at 5.4%.`}
            source={`Source: ColorAnalysisTech quiz data, rolling 12 months ending ${LAST_UPDATED}`}
            embedHeight={520}
          >
            <SeasonDistributionChart />
          </StatCard>

          {/* ===== Search trend ===== */}
          <StatCard
            id="search-trend"
            title="Search interest in 'color analysis' (last 12 months)"
            subtitle="Relative Google search interest. March 2026 set a new all-time high — up 139% year-over-year."
            source="Source: Google Trends, normalized to 100 = peak month"
          >
            <SearchTrendChart />
          </StatCard>

          {/* ===== Pricing ===== */}
          <StatCard
            id="pricing-index"
            title="What color analysis actually costs in 2026 (USD)"
            subtitle="Average price ranges across five service tiers, from AI tools to certified House of Colour sessions."
            source="Source: ColorAnalysisTech pricing index — 200+ practitioner & app listings"
          >
            <PricingChart />
          </StatCard>

          {/* ===== Method preference ===== */}
          <StatCard
            id="method-preference"
            title="AI vs in-person: how people want to be analyzed"
            subtitle="54% of newcomers now prefer AI / online color analysis — overtaking in-person for the first time."
            source={`Source: ColorAnalysisTech onboarding survey, n=${SAMPLE_SIZE.toLocaleString()}`}
          >
            <MethodPreferenceChart />
          </StatCard>

          {/* ===== Demographics ===== */}
          <StatCard
            id="demographics"
            title="Who is getting their colors analyzed?"
            subtitle="Women 25-34 are the largest cohort at 31.7%. Men now represent 10.5% of all analyses, up from 6.2% in 2024."
            source={`Source: ColorAnalysisTech onboarding survey, n=${SAMPLE_SIZE.toLocaleString()}`}
          >
            <DemographicsChart />
          </StatCard>

          {/* ===== Motivations ===== */}
          <StatCard
            id="motivations"
            title="Why people pay for color analysis"
            subtitle="61% cite capsule-wardrobe building as their primary motivation. Multi-select; sums to >100%."
            source={`Source: ColorAnalysisTech onboarding survey, n=${SAMPLE_SIZE.toLocaleString()}`}
          >
            <MotivationsChart />
          </StatCard>

          {/* CITATION / CTA */}
          <Card withBorder p="xl" radius="md" bg="var(--mantine-color-gray-0)">
            <Group justify="space-between" wrap="wrap" gap="lg">
              <Box style={{ flex: "1 1 320px" }}>
                <Title order={3} size="h4" mb="xs">
                  Citing these numbers?
                </Title>
                <Text size="sm" c="dimmed" mb="md">
                  Please link to{" "}
                  <Anchor
                    href="https://www.coloranalysistech.com/color-analysis-statistics"
                    fw={500}
                  >
                    coloranalysistech.com/color-analysis-statistics
                  </Anchor>{" "}
                  so readers can find the latest figures. Suggested attribution:
                </Text>
                <Box
                  p="md"
                  bg="white"
                  style={{
                    borderRadius: 8,
                    border: "1px solid var(--mantine-color-gray-2)",
                    fontFamily: "monospace",
                    fontSize: 12,
                  }}
                >
                  Source: ColorAnalysisTech, <em>State of Color Analysis {LAST_UPDATED}</em>.
                </Box>
              </Box>
              <Box style={{ flex: "1 1 280px" }}>
                <Title order={3} size="h4" mb="xs">
                  Need a number we don't have?
                </Title>
                <Text size="sm" c="dimmed" mb="md">
                  Working on a piece about color analysis?{" "}
                  <Anchor component={Link} href="/contact" fw={500}>
                    Get in touch
                  </Anchor>{" "}
                  — we can usually pull custom cuts within 48 hours.
                </Text>
              </Box>
            </Group>
          </Card>

          {/* METHODOLOGY */}
          <Card withBorder p="xl" radius="md">
            <Title order={2} size="h3" mb="md">
              Methodology
            </Title>
            <Stack gap="md">
              <Box>
                <Text fw={500} mb={4}>
                  Quiz data ({SAMPLE_SIZE.toLocaleString()} respondents)
                </Text>
                <Text size="sm" c="dimmed">
                  Aggregated from completed color analysis quizzes on coloranalysistech.com over
                  the rolling 12 months ending {LAST_UPDATED}. Season assignment is determined by
                  the same logic used in the free quiz and AI analysis tools. Results are
                  self-reported; users with incomplete features (e.g., missing photo) are
                  excluded.
                </Text>
              </Box>
              <Box>
                <Text fw={500} mb={4}>
                  Search trend
                </Text>
                <Text size="sm" c="dimmed">
                  Pulled from Google Trends for the query "color analysis" (worldwide), normalized
                  so the peak month = 100. The YoY growth figure compares March 2026 to March 2025
                  using the same normalization basis.
                </Text>
              </Box>
              <Box>
                <Text fw={500} mb={4}>
                  Pricing index
                </Text>
                <Text size="sm" c="dimmed">
                  Compiled from 200+ public listings across practitioner websites, Instagram
                  bookings, and color-analysis app pricing pages. Low/avg/high reflect the 10th,
                  50th, and 90th percentile within each tier.
                </Text>
              </Box>
              <Divider my="xs" />
              <Text size="xs" c="dimmed">
                Numbers are refreshed on the first of each month. If you spot something that
                looks off, tell us — corrections are credited inline.
              </Text>
            </Stack>
          </Card>

          {/* FOOTER CTA — editorial, ink + warm-accent */}
          <Card
            withBorder
            p="xl"
            radius="lg"
            style={{
              background: "linear-gradient(135deg, #1A1A2E 0%, #2C2C54 100%)",
              borderColor: "transparent",
              color: "white",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background:
                  "linear-gradient(90deg, #FFB347 0%, #F4C8D4 33%, #C4A57A 66%, #A0522D 100%)",
              }}
            />
            <Group justify="space-between" wrap="wrap" gap="md">
              <Box style={{ flex: "1 1 320px" }}>
                <Group mb={4}>
                  <IconSparkles size={20} />
                  <Text fw={600} size="lg">
                    Curious which season you are?
                  </Text>
                </Group>
                <Text size="sm" style={{ opacity: 0.9 }}>
                  These numbers come from the same engine — find your 12-season type in under two
                  minutes.
                </Text>
              </Box>
              <Button
                component={Link}
                href="/ai-color-analysis?src=stats_footer"
                size="lg"
                rightSection={<IconArrowRight size={18} />}
                style={{
                  background: "#fff",
                  color: "#1A1A2E",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                Try AI Color Analysis
              </Button>
            </Group>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
